import PropTypes from "prop-types";
import React, { useMemo } from "react";
import { ExclamationCircleIcon } from "@heroicons/react/outline";

const ComplianceTemplateOverview = ({ categories }) => {
  const normalizedCategories = useMemo(() => categories || [], [categories]);
  const totalWeight = useMemo(
    () =>
      normalizedCategories.reduce(
        (sum, category) => sum + Number(category.weight || 0),
        0
      ),
    [normalizedCategories]
  );
  const normalizedTotal = useMemo(
    () => Number(totalWeight.toFixed(2)),
    [totalWeight]
  );
  const hasImbalance =
    normalizedCategories.length > 0 && Math.abs(normalizedTotal - 100) > 0.01;

  return (
    <section className="space-y-4 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-xl font-semibold text-gray-800">
            Template Overview
          </h3>
          <p className="text-sm text-gray-600">
            Snapshot of configured compliance categories and their active tasks.
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-2">Category</th>
              <th className="px-4 py-2 text-right">Active Tasks</th>
              <th className="px-4 py-2 text-right">Weight %</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {normalizedCategories.length === 0 ? (
              <tr>
                <td className="px-4 py-3 text-sm text-gray-500" colSpan={3}>
                  No categories captured yet. Switch to Compliance Metrics to
                  add one.
                </td>
              </tr>
            ) : (
              normalizedCategories.map((category) => {
                const directTasks = Array.isArray(category.tasks)
                  ? category.tasks
                  : [];
                const sectionTasks = Array.isArray(category.sections)
                  ? category.sections.flatMap((s) => s?.tasks || [])
                  : [];
                const allTasks = [...directTasks, ...sectionTasks];
                const activeTasks = allTasks.filter(
                  (task) =>
                    task &&
                    (!task.lifecycleStatus ||
                      task.lifecycleStatus === "active")
                ).length;
                const weight = Number(category.weight || 0).toFixed(2);

                return (
                  <tr key={category.id}>
                    <td className="px-4 py-2 font-medium text-gray-700">
                      {category.name}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-600">
                      {activeTasks}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-600">
                      <span className="inline-flex items-center justify-end gap-1">
                        <span>{weight}%</span>
                        {hasImbalance && (
                          <ExclamationCircleIcon className="h-4 w-4 text-red-500" />
                        )}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

ComplianceTemplateOverview.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string,
      weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      tasks: PropTypes.array,
    })
  ),
};

ComplianceTemplateOverview.defaultProps = {
  categories: [],
};

export default ComplianceTemplateOverview;
